import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true
    }
  ]
})
export class DatepickerComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '';
  @Input() fControlName: string = '';
  @Input() isFormControl: boolean = false;
  @Input() placeholder: string = 'Select date';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string = '';
  @Input() helperText: string = '';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() formControl: FormControl = new FormControl();
  @Input() formGroup!: any;
  @Input() touchUi: boolean = false; // Set to true for mobile-friendly touch UI
  @Input() startView: 'month' | 'year' | 'multi-year' = 'month';
  @Input() dateFormat: string = 'MM/dd/yyyy'; // Default date format

  value: Date | null = null;
  isDisabled: boolean = false;

  // ControlValueAccessor methods
  onChange = (value: any) => {};
  onTouched = () => {};

  ngOnInit() {
    if (this.disabled) {
      this.isDisabled = true;
    }
  }

  onDateInput(event: MatDatepickerInputEvent<Date>) {
    const date = event.value;
    this.value = date;
    this.onChange(date);
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    const date = event.value;
    this.value = date;
    this.onChange(date);
  }

  onBlur() {
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.value = value || null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  get theFormControl(): FormControl {
    return this.formControl || this.formGroup.get(this.fControlName) as FormControl;
  }
  // Validation helpers
  get hasError(): boolean {
    return !this.theFormControl?.valid && (this.theFormControl?.dirty || this.theFormControl?.touched);
  }

  get errorMessages(): string[] {
    const errors: string[] = [];
    if (this.theFormControl?.errors) {
      if (this.theFormControl.errors['required']) {
        errors.push(`${this.label || 'Date'} is required`);
      }
      if (this.theFormControl.errors['matDatepickerParse']) {
        errors.push('Please enter a valid date');
      }
      if (this.theFormControl.errors['matDatepickerMin']) {
        errors.push('Date is before minimum allowed date');
      }
      if (this.theFormControl.errors['matDatepickerMax']) {
        errors.push('Date is after maximum allowed date');
      }
      if (this.theFormControl.errors['matDatepickerFilter']) {
        errors.push('Date is not allowed');
      }
    }
    if (this.errorMessage) {
      errors.push(this.errorMessage);
    }
    return errors;
  }

  // Utility methods
  clear() {
    this.value = null;
    this.onChange(null);
  }

  today() {
    const today = new Date();
    this.value = today;
    this.onChange(today);
  }
}
